import { useState } from "react";
import { CONFIG } from "@/lib/config";
import { SHELL, Reveal, Mono, StatusBadge } from "@/components/bits";
import { ArrowUpRight, TriangleAlert, Copy, Check } from "lucide-react";

const ROWS = [
    ["NETWORK", "ARC"],
    ["SETTLEMENT ASSET", "USDC"],
    ["OFFICIAL BUY CHANNEL", "ARGUS"],
    ["OFFICIAL CHART", "DEXSCREENER"],
];

export default function Token() {
    const [copied, setCopied] = useState(false);
    const CA = CONFIG.TOKEN_ADDRESS;
    const short = CA ? `${CA.slice(0, 10)}…${CA.slice(-8)}` : "COMING SOON";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(CA);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {}
    };

    return (
        <div data-testid="token-page">
            <section className="pt-40 pb-16 md:pt-52 md:pb-20 text-center relative overflow-hidden">
                <div className="absolute inset-0 vera-grid-bg" aria-hidden="true" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-vera-gold/8 blur-[130px]" aria-hidden="true" />
                <SHELL className="relative">
                    <Reveal y={14}>
                        <Mono className="text-vera-mut">VERA TOKEN — ARC NETWORK</Mono>
                    </Reveal>
                    <Reveal delay={0.06}>
                        <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight mt-6 mb-8 text-gold-grad">$VERA</h1>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="flex justify-center" data-testid="token-live-badge">
                            <StatusBadge status="LIVE" />
                        </div>
                    </Reveal>
                </SHELL>
            </section>

            <section className="pb-24">
                <SHELL>
                    <div className="max-w-2xl mx-auto">
                        <Reveal>
                            <div className="glass-card p-8 md:p-10 mb-8" data-testid="token-info-card">
                                <dl>
                                    <div className="flex items-center justify-between gap-3 flex-wrap py-4 border-b border-white/8" data-testid="token-address-row">
                                        <dt className="font-mono text-[10px] tracking-[0.2em] text-vera-mut">CONTRACT ADDRESS</dt>
                                        <dd className="flex items-center gap-2.5">
                                            <span className="font-mono text-[11px] tracking-[0.05em] text-vera-gold2" title={CA} data-testid="token-contract-address">
                                                {short}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={copy}
                                                data-testid="token-copy-address"
                                                aria-label="Copy contract address"
                                                className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.15em] border border-white/15 px-2.5 py-1.5 text-vera-mut hover:text-white hover:border-vera-soft/60 transition-all"
                                            >
                                                {copied ? <Check className="w-3 h-3 text-[#10B981]" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
                                                {copied ? "COPIED" : "COPY"}
                                            </button>
                                        </dd>
                                    </div>
                                    {ROWS.map(([k, v]) => (
                                        <div key={k} className="flex items-center justify-between gap-4 py-4 border-b border-white/8 last:border-0">
                                            <dt className="font-mono text-[10px] tracking-[0.2em] text-vera-mut">{k}</dt>
                                            <dd className="font-mono text-[11px] tracking-[0.15em] text-vera-white">{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </Reveal>

                        <Reveal delay={0.08}>
                            <div className="flex flex-col sm:flex-row gap-4 mb-10">
                                <a
                                    href={CONFIG.BUY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="token-buy-button"
                                    className="flex-1 inline-flex items-center justify-center gap-2.5 font-mono text-[11px] tracking-[0.22em] px-7 py-4 border border-vera-gold/60 text-vera-gold2 bg-vera-gold/5 hover:bg-vera-gold/15 transition-all"
                                >
                                    BUY VERA <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                                </a>
                                <a
                                    href={CONFIG.DEXSCREENER_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="token-chart-button"
                                    className="flex-1 inline-flex items-center justify-center gap-2.5 font-mono text-[11px] tracking-[0.22em] px-7 py-4 border border-vera-blue/50 text-[#A8D2FF] bg-vera-blue/5 hover:bg-vera-blue/15 transition-all"
                                >
                                    VIEW CHART <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                                </a>
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="border border-dashed border-vera-gold/40 bg-vera-gold/5 px-6 py-5 flex items-start gap-4" data-testid="token-safety-note">
                                <TriangleAlert className="w-4 h-4 text-vera-gold mt-0.5 shrink-0" aria-hidden="true" />
                                <p className="text-xs text-vera-mut leading-relaxed">
                                    This is the only official VERA token contract address, published by the VERA team and shown on the official Token page.
                                    Beware of copies and fake addresses claiming to be VERA. Always verify the address before any transaction.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </SHELL>
            </section>
        </div>
    );
}
